import type { ReactNode } from 'react'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { DataGrid, type Column } from '../../platform/primitives/DataGrid'
import { Money } from '../../platform/primitives/Money'
import type { Axis, QueryTableRow } from './reportView'
import './reports.css'

type Props = {
  rows: QueryTableRow[]
  axis: Axis
  /** The first column's header, for example "Merchant". */
  dimension: string
  /** The grid's accessible name, for example "Spending by merchant". */
  title: string
  currency: string
  phone: boolean
  /** Where a row opens, or null for a plain label (Uncategorised, Other, Total). Needs a router when it returns a path. */
  rowHref?: (row: QueryTableRow) => string | null
}

/**
 * The rows × period (or rows × owner) table (M8). Every figure is passed in from the server: a cell is a
 * `series` entry or an owner `cells` entry, a row's Total column is its `amount`, the Total row is the
 * server's column totals and the grand total is `total.amount` (P6). Nothing here adds two amounts, and
 * `DataGrid`'s group subtotal is not used because it reconciles by summing. Cells carry no currency symbol
 * (B11): the caption names it once. The none row (Uncategorised, No merchant, Untagged) has its own cells.
 */

function Cell({ cents, currency }: { cents: number; currency: string }) {
  return <Money cents={cents} currency={currency} display="whole" symbol={false} />
}

function cellAt(row: QueryTableRow, i: number, currency: string): ReactNode {
  return <Cell cents={row.cells[i] ?? 0} currency={currency} />
}

function nameOf(row: QueryTableRow, href: string | null = null): ReactNode {
  if (row.kind === 'none') return row.label
  if (row.kind === 'other') return `Other ${row.noun} (${row.count})`
  if (row.kind === 'total') return 'Total'
  return (
    <span className="rp-table__name">
      {href === null ? row.label : <InlineLink to={href}>{row.label}</InlineLink>}
      {row.detail !== null && <span className="rp-table__parent">{row.detail}</span>}
    </span>
  )
}

const keyOf = (r: QueryTableRow): string => (r.kind === 'row' ? r.key : r.kind)

function rowClass(r: QueryTableRow): string | undefined {
  if (r.kind === 'total') return 'rp-table__total'
  if (r.kind === 'none') return 'rp-table__uncat'
  return undefined
}

function Heading({ label, partial }: { label: string; partial: boolean }) {
  return (
    <>
      <span>{label}</span>
      {partial && ' '}
      {partial && <span className="rp-table__partial">so far</span>}
    </>
  )
}

export function ReportTable({ rows, axis, dimension, title, currency, phone, rowHref }: Props) {
  const labels = axis.labels
  const current = axis.kind === 'period' ? axis.current : null
  const caption = <p className="rp-table__caption">{`Amounts in ${currency}`}</p>

  if (phone) {
    return (
      <div className="rp-table">
        {caption}
        <ul className="rp-table__cards" aria-label={title}>
          {rows.map((r) => (
            <li key={keyOf(r)} className={`rp-table__card ${rowClass(r) ?? ''}`.trim()}>
              <div className="rp-table__card-head">
                <span>{nameOf(r, rowHref?.(r) ?? null)}</span>
                <Cell cents={r.totalCents} currency={currency} />
              </div>
              <dl className="rp-table__facts">
                {labels.map((l, i) => (
                  <div key={l + i} className="rp-table__fact">
                    <dt>
                      <Heading label={l} partial={i === current} />
                    </dt>
                    <dd>{cellAt(r, i, currency)}</dd>
                  </div>
                ))}
              </dl>
            </li>
          ))}
        </ul>
      </div>
    )
  }

  const columns: Column<QueryTableRow>[] = [
    {
      key: 'name',
      header: dimension,
      width: 'minmax(min(160px, 100%), 1.4fr)',
      render: (r) => nameOf(r, rowHref?.(r) ?? null),
    },
    ...labels.map(
      (l, i): Column<QueryTableRow> => ({
        key: `p${i}`,
        header: <Heading label={l} partial={i === current} />,
        width: 'minmax(64px, 1fr)',
        align: 'end',
        render: (r) => cellAt(r, i, currency),
      }),
    ),
    {
      key: 'total',
      header: 'Total',
      width: 'minmax(64px, 1fr)',
      align: 'end',
      render: (r) => <Cell cents={r.totalCents} currency={currency} />,
    },
  ]

  return (
    <div className="rp-table">
      {caption}
      <div className="rp-table__scroll">
        <DataGrid
          columns={columns}
          groups={[{ label: '', rows }]}
          rowKey={keyOf}
          label={title}
          empty={null}
          rowClassName={rowClass}
        />
      </div>
    </div>
  )
}
